import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './BannerSlider.css';

const API = process.env.REACT_APP_API_URL;

const DEFAULT_BANNERS = [
  {
    id: 'default-1',
    image_url: null,
    link_url: '',
    duration: 6,
    _content: (
      <div className="bs-default bs-default--brand">
        <div className="bs-default-accent" />
        <div className="bs-default-icon">🍌</div>
        <div className="bs-default-title">BanaPrecios</div>
        <div className="bs-default-sub">Mercado bananero del Ecuador</div>
        <div className="bs-default-pill">Precios en tiempo real</div>
      </div>
    ),
  },
  {
    id: 'default-2',
    image_url: null,
    link_url: '',
    duration: 6,
    _content: (
      <div className="bs-default bs-default--feature">
        <div className="bs-default-accent" />
        <div className="bs-default-icon">📊</div>
        <div className="bs-default-title">Análisis completo</div>
        <div className="bs-default-sub">Semanal · Mensual · Histórico</div>
        <div className="bs-default-pill">Para productores</div>
      </div>
    ),
  },
  {
    id: 'default-3',
    image_url: null,
    link_url: '',
    duration: 6,
    _content: (
      <div className="bs-default bs-default--cta">
        <div className="bs-default-accent" />
        <div className="bs-default-icon">🌿</div>
        <div className="bs-default-title">Únete gratis</div>
        <div className="bs-default-sub">Reportes, Q's y más</div>
        <div className="bs-default-pill">banaprecios.com</div>
      </div>
    ),
  },
];

const BannerSlider = ({ position, side = 'right' }) => {
  const [items, setItems]     = useState([]);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused]   = useState(false);
  const [loaded, setLoaded]   = useState(false);
  const [fading, setFading]   = useState(false);

  // Separate refs for the two timers so they never interfere
  const advanceRef  = useRef(null);
  const progressRef = useRef(null);
  const startRef    = useRef(null);

  useEffect(() => {
    axios.get(`${API}/banners/?position=${position}`)
      .then(r => setItems(r.data.length > 0 ? r.data : DEFAULT_BANNERS))
      .catch(() => setItems(DEFAULT_BANNERS))
      .finally(() => setLoaded(true));
  }, [position]);

  const durationMs = ((items[current]?.duration) || 6) * 1000;
  const count = items.length;

  // ── Progress bar via rAF (purely visual, no advance logic) ──────
  useEffect(() => {
    if (!loaded || paused || count <= 1) { setProgress(0); return; }

    startRef.current = Date.now();
    setProgress(0);

    const tick = () => {
      const pct = Math.min(((Date.now() - startRef.current) / durationMs) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        progressRef.current = requestAnimationFrame(tick);
      }
    };
    progressRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(progressRef.current);
  // eslint-disable-next-line
  }, [loaded, paused, current, count, durationMs]);

  // ── Slide advance via setTimeout (independent of rAF above) ─────
  useEffect(() => {
    if (!loaded || paused || count <= 1) return;

    advanceRef.current = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent(c => (c + 1) % count);
        setFading(false);
      }, 200); // fade duration
    }, durationMs);

    return () => clearTimeout(advanceRef.current);
  // eslint-disable-next-line
  }, [loaded, paused, current, count, durationMs]);

  const goTo = (i) => {
    clearTimeout(advanceRef.current);
    cancelAnimationFrame(progressRef.current);
    setFading(true);
    setTimeout(() => {
      setCurrent(i);
      setProgress(0);
      setFading(false);
    }, 150);
  };

  if (!loaded || items.length === 0) return <div className={`bs-col bs-col--${side}`} />;

  const item = items[current];
  const isDefault = !!item._content;

  const slide = (
    <div className={`bs-slide ${fading ? 'bs-slide--fading' : ''}`}>
      {isDefault ? (
        item._content
      ) : (
        <img src={item.image_url} alt="banner" className="bs-image" />
      )}
      {count > 1 && (
        <div className="bs-progress">
          <div className="bs-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`bs-col bs-col--${side}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="bs-ad-label">Publicidad</div>

      {item.link_url
        ? <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="bs-link">{slide}</a>
        : slide
      }

      {count > 1 && (
        <div className="bs-dots">
          {items.map((_, i) => (
            <button
              key={i}
              className={`bs-dot${i === current ? ' bs-dot--on' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
